import { jsPDF } from "jspdf";

export type ReceiptData = {
  userName?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  school?: string | null;
  provider?: string | null; // "razorpay" | "paypal"
  orderId?: string | null;
  paymentId?: string | null;
  paypalOrderId?: string | null;
  paypalCaptureId?: string | null;
  currency?: string | null;
  listPrice?: number | null;
  discountApplied?: number | null;
  finalPrice?: number | null;
  couponCode?: string | null;
  status?: string | null;
  paidAt?: string | null; // ISO
  receiptNumber?: string | null;
};

const fmtMoney = (amount: number | null | undefined, currency?: string | null) => {
  if (amount == null) return "—";
  const code = (currency || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat(code === "INR" ? "en-IN" : "en-US", {
      style: "currency",
      currency: code,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${code} ${amount}`;
  }
};

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export function generateReceiptPdf(d: ReceiptData) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 48;
  let y = M;

  // Header band
  doc.setFillColor(15, 23, 42); // slate-900
  doc.rect(0, 0, W, 96, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("WeKIT™", M, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text("AI Career Discovery Assessment", M, 70);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Payment Receipt", W - M, 50, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Issued ${fmtDate(new Date().toISOString())}`, W - M, 68, { align: "right" });

  y = 120;
  doc.setTextColor(15, 23, 42);

  // Receipt meta
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Receipt #", M, y);
  doc.setFont("helvetica", "normal");
  doc.text(d.receiptNumber || d.paymentId || d.orderId || "—", M + 70, y);

  doc.setFont("helvetica", "bold");
  doc.text("Status", W / 2, y);
  doc.setFont("helvetica", "normal");
  doc.text((d.status || "success").toUpperCase(), W / 2 + 60, y);
  y += 18;

  doc.setFont("helvetica", "bold");
  doc.text("Paid on", M, y);
  doc.setFont("helvetica", "normal");
  doc.text(fmtDate(d.paidAt), M + 70, y);

  doc.setFont("helvetica", "bold");
  doc.text("Method", W / 2, y);
  doc.setFont("helvetica", "normal");
  doc.text((d.provider || "razorpay").toUpperCase(), W / 2 + 60, y);
  y += 28;

  // Bill to / From sections
  const colW = (W - M * 2 - 24) / 2;
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.8);
  doc.roundedRect(M, y, colW, 110, 6, 6);
  doc.roundedRect(M + colW + 24, y, colW, 110, 6, 6);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text("BILLED TO", M + 12, y + 18);
  doc.text("FROM", M + colW + 24 + 12, y + 18);

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  const billLines = [
    d.userName || "—",
    d.email || "",
    d.phone || "",
    [d.school, d.city].filter(Boolean).join(" · "),
  ].filter(Boolean);
  billLines.forEach((line, i) => doc.text(String(line), M + 12, y + 36 + i * 16));

  const fromLines = [
    "WeKIT™",
    "support@wekit.ai",
    "https://wekit.ai",
  ];
  fromLines.forEach((line, i) =>
    doc.text(line, M + colW + 24 + 12, y + 36 + i * 16),
  );

  y += 134;

  // Line items table
  doc.setFillColor(241, 245, 249);
  doc.rect(M, y, W - M * 2, 26, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text("DESCRIPTION", M + 12, y + 17);
  doc.text("AMOUNT", W - M - 12, y + 17, { align: "right" });
  y += 26;

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  const drawRow = (label: string, value: string, bold = false) => {
    if (bold) doc.setFont("helvetica", "bold");
    doc.text(label, M + 12, y + 18);
    doc.text(value, W - M - 12, y + 18, { align: "right" });
    if (bold) doc.setFont("helvetica", "normal");
    doc.setDrawColor(241, 245, 249);
    doc.line(M, y + 30, W - M, y + 30);
    y += 30;
  };

  drawRow(
    "AI Career Discovery Assessment — Full Access",
    fmtMoney(d.listPrice ?? d.finalPrice, d.currency),
  );

  if (d.discountApplied && Number(d.discountApplied) > 0) {
    drawRow(
      `Discount${d.couponCode ? ` (${d.couponCode})` : ""}`,
      `- ${fmtMoney(Number(d.discountApplied), d.currency)}`,
    );
  }

  y += 6;
  doc.setFontSize(13);
  drawRow("Total paid", fmtMoney(d.finalPrice, d.currency), true);

  y += 8;

  // Transaction details
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(100, 116, 139);
  doc.text("TRANSACTION DETAILS", M, y);
  y += 16;
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "normal");

  const txn: Array<[string, string | null | undefined]> = [
    ["Provider", (d.provider || "").toUpperCase()],
    ["Order ID", d.orderId || d.paypalOrderId],
    ["Payment ID", d.paymentId || d.paypalCaptureId],
    ["Currency", (d.currency || "INR").toUpperCase()],
  ];
  txn.forEach(([k, v]) => {
    if (!v) return;
    doc.setFont("helvetica", "bold");
    doc.text(k, M, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(v), M + 90, y);
    y += 16;
  });

  // Footer
  const footY = doc.internal.pageSize.getHeight() - 60;
  doc.setDrawColor(226, 232, 240);
  doc.line(M, footY, W - M, footY);
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    "Thank you for choosing WeKIT™. This receipt confirms your payment for full access to the AI Career Discovery Assessment.",
    M,
    footY + 18,
    { maxWidth: W - M * 2 },
  );
  doc.text(
    "Questions about this receipt? Email support@wekit.ai",
    M,
    footY + 40,
  );

  const fileName = `wekit-receipt-${d.paymentId || d.orderId || Date.now()}.pdf`;
  doc.save(fileName);
}
